import { MAX_CANVAS_STACK_DEPTH } from "@/types/agenticUiState.ts";
import type { CanvasDisplayMode } from "@/types/agenticUiState.ts";
import { useCallback, useMemo, useRef, useState } from "react";
import { parseCanvasContent } from "./canvasContent.ts";
import type { CanvasActionContextValue, PushResult } from "./canvasContext.tsx";

/** Stable empty stack for the resting state, so consumers reading the returned
 *  `canvasStack` get the same reference across renders. */
const EMPTY_STACK: string[] = [];

/**
 * A canvas held in component state, for a surface with nothing to persist into.
 *
 * The thread canvas is per-thread and server-backed (`useThreadCanvas`); the
 * calendar pages have no thread, and their canvas content is deliberately
 * ephemeral — an event opened here does not survive navigating away. That makes
 * this a plain `useState` rather than a query cache, but the action surface is
 * identical, so every canvas consumer (cards, links, nav boundaries) works
 * against it unchanged.
 *
 * Visibility is fixed open: this canvas *is* the page's calendar column, so there
 * is no panel to collapse. `open`/`close` are accepted and ignored rather than
 * omitted, because the context type is what every consumer codes against.
 *
 * `initialDisplay` is the mode a card opens at, for a host whose box is already
 * card-sized — a right sidebar, a drilled-in panel. Bounded inside one of those,
 * a card is a scrim around something narrower than the panel it covers, so those
 * hosts open at `full` and the header's toggle still shrinks it. It is a
 * starting point only: once toggled, the choice is this canvas's own.
 */
export const useEphemeralCanvas = ({
  initialDisplay = "card",
}: { initialDisplay?: CanvasDisplayMode } = {}): {
  canvasStack: string[];
  isCanvasOpen: boolean;
  isFullscreen: boolean;
  canvasActions: CanvasActionContextValue;
} => {
  const [canvasStack, setCanvasStack] = useState<string[]>(EMPTY_STACK);
  const [display, setDisplay] = useState<CanvasDisplayMode>(initialDisplay);

  // `push` has to read the current stack to reject a duplicate top or a
  // max-depth push *before* deciding its return value, which a functional
  // setState can't report back. A ref mirrors the state for those reads while
  // keeping the action identities stable.
  const stackRef = useRef<string[]>(canvasStack);
  const setStack = useCallback((next: (prev: string[]) => string[]) => {
    setCanvasStack((prev) => {
      const value = next(prev);
      stackRef.current = value;
      return value;
    });
  }, []);

  /** An outside writer presenting a fresh thing: replace the stack with it. */
  const resetTo = useCallback(
    (urn: string) => {
      if (!parseCanvasContent(urn)) return false;
      setStack(() => [urn]);
      return true;
    },
    [setStack],
  );

  /** Drill down: layer a URN over the current top. See {@link PushResult}. */
  const push = useCallback(
    (urn: string): PushResult => {
      if (!parseCanvasContent(urn)) return "unsupported";
      const stack = stackRef.current;
      // A self-link to the current top would stack a pointless duplicate.
      if (stack.at(-1) === urn) return "duplicate";
      if (stack.length >= MAX_CANVAS_STACK_DEPTH) return "max-depth";
      setStack((prev) => [...prev, urn]);
      return "pushed";
    },
    [setStack],
  );

  const pop = useCallback(() => setStack((prev) => prev.slice(0, -1)), [setStack]);
  const clear = useCallback(() => setStack(() => EMPTY_STACK), [setStack]);

  /** Drop every layer addressing `urn` — the card found its target is gone. The
   *  dead layer need not be the top one, so this filters rather than pops. */
  const dropContent = useCallback(
    (urn: string) => setStack((prev) => prev.filter((entry) => entry !== urn)),
    [setStack],
  );

  const noop = useCallback(() => {}, []);
  const setDisplayMode = useCallback((mode: CanvasDisplayMode) => setDisplay(mode), []);

  // Home contributes no view-context to the agent today; unifying that with the
  // thread's snapshot is BIG-6605's, and reporting one here would silently
  // change what the agent sees on this surface.
  const getSnapshot = useCallback(() => undefined, []);

  const canvasActions = useMemo<CanvasActionContextValue>(
    () => ({
      resetTo,
      push,
      pop,
      clear,
      dropContent,
      open: noop,
      close: noop,
      setDisplayMode,
      getSnapshot,
    }),
    [resetTo, push, pop, clear, dropContent, noop, setDisplayMode, getSnapshot],
  );

  return { canvasStack, isCanvasOpen: true, isFullscreen: display === "full", canvasActions };
};
